import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ShoppingBag, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "../context/CartContext";

const Logo = ({ light }) => (
  <Link to="/" data-testid="brand-logo" className="flex flex-col leading-none select-none">
    <span
      className={`font-sans font-black tracking-[0.14em] text-lg md:text-xl ${
        light ? "text-cream" : "text-ink"
      }`}
    >
      PRESTIGE
    </span>
    <span
      className={`font-serif italic text-xs md:text-sm tracking-[0.28em] ${
        light ? "text-cream/80" : "text-faded"
      }`}
    >
      wellness co.
    </span>
  </Link>
);

const links = [
  { label: "Shop the range", to: "/#products" },
  { label: "Bundles", to: "/#bundles" },
  { label: "Our ritual", to: "/#manifesto" },
  { label: "The Experience", to: "/experience" },
  { label: "My account", to: "/account" },
  { label: "Contact us", to: "/#contact" },
];

export const Navbar = ({ heroLight = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, setOpen } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = heroLight && !scrolled;

  const go = (to) => {
    setMenuOpen(false);
    if (to.startsWith("/#")) {
      const id = to.split("#")[1];
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-cream/80 backdrop-blur-xl border-b border-forest/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Logo light={light} />
          <div className="flex items-center gap-3 md:gap-5">
            <button
              data-testid="open-cart-button"
              onClick={() => setOpen(true)}
              className={`relative p-2 transition-colors ${light ? "text-cream" : "text-ink"}`}
              aria-label="Open cart"
            >
              <ShoppingBag strokeWidth={1.5} className="w-6 h-6" />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-0.5 -right-0.5 bg-forest text-cream text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="open-menu-button"
              onClick={() => setMenuOpen(true)}
              className={`p-2 transition-colors ${light ? "text-cream" : "text-ink"}`}
              aria-label="Open menu"
            >
              <Menu strokeWidth={1.5} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="menu-overlay"
            className="fixed inset-0 z-[70] bg-forest text-cream flex flex-col"
            initial={{ clipPath: "circle(0% at 100% 0%)" }}
            animate={{ clipPath: "circle(150% at 100% 0%)" }}
            exit={{ clipPath: "circle(0% at 100% 0%)" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="max-w-7xl w-full mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
              <Logo light />
              <button
                data-testid="close-menu-button"
                onClick={() => setMenuOpen(false)}
                className="p-2"
                aria-label="Close menu"
              >
                <X strokeWidth={1.5} className="w-7 h-7" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 md:px-12 gap-2 md:gap-4">
              {links.map((l, i) => (
                <motion.button
                  key={l.label}
                  data-testid={`menu-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l.to)}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className="text-left font-sans font-extrabold tracking-tight text-5xl md:text-7xl text-cream/90 hover:text-cream transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="px-6 md:px-12 py-8 border-t border-cream/15 font-serif italic text-cream/70">
              info@prestigewellnessco.co.uk
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
